import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useT } from "@/i18n";
import { useBrand } from "@/context/BrandContext";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const EMPTY = {
  company_id: "",
  brand: "bb",
  nombre: "",
  direccion: { calle: "", ciudad: "", estado: "CA", zip: "" },
  cantidad_unidades: 0,
  site_manager: { nombre: "", email: "", telefono: "" },
  email_facturacion: "",
  email_estimados: "",
  email_facturas: "",
  email_cobros: "",
  telefono_facturacion: "",
  notificacion_cliente: { canal: "email", intervalo_dias: 7 },
  notas_acceso: "",
  notas: "",
  piloto: false,
  tipo: "edificio",
};

const Field = ({ label, children, className }) => (
  <div className={className}>
    <Label className="mb-1.5 block text-[12px] text-ink-tertiary">{label}</Label>
    {children}
  </div>
);

export const PropertyDialog = ({ open, onOpenChange, property }) => {
  const { t } = useT();
  const qc = useQueryClient();
  const { brandParam } = useBrand();
  const [form, setForm] = useState(EMPTY);

  const { data: companies } = useQuery({
    queryKey: ["companies-options"],
    queryFn: async () => (await api.get("/companies", { params: { page_size: 200, status: "active" } })).data,
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    setForm(
      property
        ? {
            company_id: property.company_id,
            brand: property.brand,
            nombre: property.nombre,
            direccion: property.direccion,
            cantidad_unidades: property.cantidad_unidades ?? 0,
            site_manager: property.site_manager || { nombre: "", email: "", telefono: "" },
            email_facturacion: property.email_facturacion || "",
            email_estimados: property.email_estimados || "",
            email_facturas: property.email_facturas || "",
            email_cobros: property.email_cobros || "",
            telefono_facturacion: property.telefono_facturacion || "",
            notificacion_cliente: property.notificacion_cliente || { canal: "email", intervalo_dias: 7 },
            notas_acceso: property.notas_acceso || "",
            notas: property.notas || "",
            piloto: !!property.piloto,
            tipo: property.tipo || "edificio",
          }
        : { ...EMPTY, brand: brandParam || "bb" }
    );
  }, [open, property, brandParam]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));
  const setNested = (group, key, value) =>
    setForm((f) => ({ ...f, [group]: { ...f[group], [key]: value } }));

  const mutation = useMutation({
    mutationFn: async () => {
      const payload = {
        ...form,
        cantidad_unidades: Number(form.cantidad_unidades) || 0,
        notificacion_cliente: {
          ...form.notificacion_cliente,
          intervalo_dias: Number(form.notificacion_cliente.intervalo_dias) || 7,
        },
      };
      if (property) return (await api.patch(`/properties/${property.id}`, payload)).data;
      return (await api.post("/properties", payload)).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["properties"] });
      qc.invalidateQueries({ queryKey: ["property"] });
      qc.invalidateQueries({ queryKey: ["overview"] });
      toast.success(property ? t("app.saved") : t("app.created"));
      onOpenChange(false);
    },
    onError: (e) => toast.error(apiError(e, t("app.error"))),
  });

  const valid = form.nombre.trim() && form.company_id && form.direccion.calle.trim() && form.direccion.ciudad.trim();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="property-dialog" className="max-h-[88vh] max-w-[680px] overflow-y-auto rounded-[8px]">
        <DialogHeader>
          <DialogTitle className="text-[16px]">
            {property ? t("properties.edit") : t("properties.new")}
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-3.5">
          <Field label={t("app.name")} className="col-span-2">
            <Input
              data-testid="property-name-input"
              value={form.nombre}
              onChange={(e) => set("nombre", e.target.value)}
              className="h-9 rounded-[6px] text-[13.5px]"
            />
          </Field>
          <Field label={t("properties.company")}>
            <Select value={form.company_id} onValueChange={(v) => set("company_id", v)}>
              <SelectTrigger data-testid="property-company-select" className="h-9 rounded-[6px] text-[13.5px]">
                <SelectValue placeholder={t("properties.company")} />
              </SelectTrigger>
              <SelectContent>
                {(companies?.items || []).map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.nombre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t("app.brand")}>
            <Select value={form.brand} onValueChange={(v) => set("brand", v)}>
              <SelectTrigger data-testid="property-brand-select" className="h-9 rounded-[6px] text-[13.5px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bb">B&amp;B</SelectItem>
                <SelectItem value="betos">Betos</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          {/* Fix U (2026-10-05): una casa de un cliente particular no es un edificio */}
          <Field label={t("properties.kind")}>
            <Select value={form.tipo} onValueChange={(v) => set("tipo", v)}>
              <SelectTrigger data-testid="property-kind-select" className="h-9 rounded-[6px] text-[13.5px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="edificio">{t("properties.kinds.edificio")}</SelectItem>
                <SelectItem value="casa">{t("properties.kinds.casa")}</SelectItem>
                <SelectItem value="comercio">{t("properties.kinds.comercio")}</SelectItem>
              </SelectContent>
            </Select>
          </Field>

          <Field label={t("properties.street")} className="col-span-2">
            <Input
              data-testid="property-street-input"
              value={form.direccion.calle}
              onChange={(e) => setNested("direccion", "calle", e.target.value)}
              className="h-9 rounded-[6px] text-[13.5px]"
            />
          </Field>
          <Field label={t("properties.city")}>
            <Input
              data-testid="property-city-input"
              value={form.direccion.ciudad}
              onChange={(e) => setNested("direccion", "ciudad", e.target.value)}
              className="h-9 rounded-[6px] text-[13.5px]"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("properties.state")}>
              <Input
                data-testid="property-state-input"
                value={form.direccion.estado}
                onChange={(e) => setNested("direccion", "estado", e.target.value)}
                className="h-9 rounded-[6px] text-[13.5px]"
              />
            </Field>
            <Field label={t("properties.zip")}>
              <Input
                data-testid="property-zip-input"
                value={form.direccion.zip}
                onChange={(e) => setNested("direccion", "zip", e.target.value)}
                className="h-9 rounded-[6px] text-[13.5px]"
              />
            </Field>
          </div>

          <div className="col-span-2 border-t border-line pt-3">
            <p className="mb-2.5 text-[12px] font-medium uppercase tracking-[0.05em] text-ink-tertiary">
              {t("properties.site_manager")}
            </p>
            <div className="grid grid-cols-3 gap-3">
              <Field label={t("app.name")}>
                <Input
                  data-testid="property-sm-name-input"
                  value={form.site_manager.nombre || ""}
                  onChange={(e) => setNested("site_manager", "nombre", e.target.value)}
                  className="h-9 rounded-[6px] text-[13.5px]"
                />
              </Field>
              <Field label={t("app.email")}>
                <Input
                  data-testid="property-sm-email-input"
                  value={form.site_manager.email || ""}
                  onChange={(e) => setNested("site_manager", "email", e.target.value)}
                  className="h-9 rounded-[6px] text-[13.5px]"
                />
              </Field>
              <Field label={t("app.phone")}>
                <Input
                  data-testid="property-sm-phone-input"
                  value={form.site_manager.telefono || ""}
                  onChange={(e) => setNested("site_manager", "telefono", e.target.value)}
                  className="h-9 rounded-[6px] text-[13.5px]"
                />
              </Field>
            </div>
          </div>

          <Field label={t("properties.billing_email")}>
            <Input
              data-testid="property-billing-email-input"
              value={form.email_facturacion || ""}
              onChange={(e) => set("email_facturacion", e.target.value)}
              className="h-9 rounded-[6px] text-[13.5px]"
            />
          </Field>
          <Field label={t("properties.billing_phone")}>
            <Input
              data-testid="property-billing-phone-input"
              value={form.telefono_facturacion || ""}
              onChange={(e) => set("telefono_facturacion", e.target.value)}
              className="h-9 rounded-[6px] text-[13.5px]"
            />
          </Field>
          <Field label={t("properties.email_estimados")}>
            <Input
              data-testid="property-email-estimados-input"
              value={form.email_estimados || ""}
              onChange={(e) => set("email_estimados", e.target.value)}
              className="h-9 rounded-[6px] text-[13.5px]"
            />
          </Field>
          <Field label={t("properties.email_facturas")}>
            <Input
              data-testid="property-email-facturas-input"
              value={form.email_facturas || ""}
              onChange={(e) => set("email_facturas", e.target.value)}
              className="h-9 rounded-[6px] text-[13.5px]"
            />
          </Field>
          <Field label={t("properties.email_cobros")}>
            <Input
              data-testid="property-email-cobros-input"
              value={form.email_cobros || ""}
              onChange={(e) => set("email_cobros", e.target.value)}
              className="h-9 rounded-[6px] text-[13.5px]"
            />
          </Field>

          <Field label={t("properties.declared_units")}>
            <Input
              data-testid="property-units-input"
              type="number"
              value={form.cantidad_unidades}
              onChange={(e) => set("cantidad_unidades", e.target.value)}
              className="h-9 rounded-[6px] text-[13.5px]"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("properties.notify_channel")}>
              <Select
                value={form.notificacion_cliente.canal}
                onValueChange={(v) => setNested("notificacion_cliente", "canal", v)}
              >
                <SelectTrigger data-testid="property-notify-channel" className="h-9 rounded-[6px] text-[13.5px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="sms">SMS</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label={t("properties.notify_interval")}>
              <Input
                data-testid="property-notify-interval"
                type="number"
                value={form.notificacion_cliente.intervalo_dias}
                onChange={(e) => setNested("notificacion_cliente", "intervalo_dias", e.target.value)}
                className="h-9 rounded-[6px] text-[13.5px]"
              />
            </Field>
          </div>

          {/* Fix N (2026-09-28): con el modo piloto prendido, solo estos edificios reciben avisos automáticos. */}
          <div className="col-span-2 flex items-center justify-between gap-3 rounded-[6px] border border-line bg-surface-sunken px-3 py-2">
            <div>
              <p className="text-[13px] text-ink">{t("properties.pilot")}</p>
              <p className="text-[11.5px] text-ink-tertiary">{t("properties.pilot_hint")}</p>
            </div>
            <Switch data-testid="property-pilot-switch" checked={!!form.piloto}
              onCheckedChange={(v) => set("piloto", v)} />
          </div>

          <Field label={t("properties.access_notes")} className="col-span-2">
            <Textarea
              data-testid="property-access-notes-input"
              value={form.notas_acceso || ""}
              onChange={(e) => set("notas_acceso", e.target.value)}
              className="min-h-[70px] rounded-[6px] text-[13.5px]"
            />
          </Field>
          <Field label={t("app.notes")} className="col-span-2">
            <Textarea
              data-testid="property-notes-input"
              value={form.notas || ""}
              onChange={(e) => set("notas", e.target.value)}
              className="min-h-[60px] rounded-[6px] text-[13.5px]"
            />
          </Field>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            data-testid="property-cancel-button"
            className="h-9 rounded-[6px] text-[13px]"
            onClick={() => onOpenChange(false)}
          >
            {t("app.cancel")}
          </Button>
          <Button
            data-testid="property-save-button"
            disabled={!valid || mutation.isPending}
            onClick={() => mutation.mutate()}
            className="h-9 rounded-[6px] bg-brand text-[13px] hover:bg-brand-hover"
          >
            {t("app.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
